import { FC } from 'react';
import { Accordion } from '@trussworks/react-uswds';

export interface ValidationError {
  error: string;
  rowIndex: string;
  columnName: string;
}

interface ValidationErrorContentProps {
  errors?: ValidationError[];
  fileName?: string;
}
export const CsvValidationModal: FC<ValidationErrorContentProps> = ({ errors, fileName }) => {
  if (!errors) return null;

  const groupByRow = (errorList: ValidationError[]) => {
    return errorList.reduce<Record<string, ValidationError[]>>((acc, err) => {
      acc[err.rowIndex] = acc[err.rowIndex] ? [...acc[err.rowIndex], err] : [err];
      return acc;
    }, {});
  };

  const invalidRows = errors.filter((err) => err.error.includes('not valid') || err.error.includes('letters'));
  const missingRows = errors.filter((err) => err.error.includes('missing'));

  const accordionItems = [];

  if (invalidRows.length > 0) {
    const groupedInvalid = groupByRow(invalidRows);
    accordionItems.push({
      id: 'validation-error-invalid',
      headingLevel: 'h6' as const,
      title: `Invalid Values (${invalidRows.length})`,
      content: (
        <div>
          {Object.entries(groupedInvalid).map(([rowIndex, errs]) => (
            <div key={rowIndex} className='margin-bottom-1'>
              <span className='text-bold'>Row {rowIndex}:</span>
              <ul>
                {errs.map((err, i) => (
                  <li key={`${rowIndex}-${err.columnName}-${i}`}>{`${err.columnName} - ${err.error}`}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ),
      expanded: true,
    });
  }

  if (missingRows.length > 0) {
    const groupedMissing = groupByRow(missingRows);
    accordionItems.push({
      id: 'validation-error-missing',
      headingLevel: 'h6' as const,
      title: `Missing Values (${missingRows.length})`,
      content: (
        <div>
          {Object.entries(groupedMissing).map(([rowIndex, errs]) => (
            <div key={rowIndex} className='margin-bottom-1'>
              <span className='text-bold'>Row {rowIndex}:</span>
              <ul>
                {errs.map((err, i) => (
                  <li key={`${rowIndex}-${err.columnName}-${i}`}>{`${err.columnName} - ${err.error}`}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ),
      expanded: true,
    });
  }

  return (
    <div className='usa-prose margin-bottom-0 padding-bottom-0'>
      <div className='margin-bottom-1'>
        <div className='usa-alert usa-alert--slim callout margin-bottom-1 padding-2'>
          The uploaded file contains missing or invalid data. Please fix these issues using the table or by re-uploading
          the file with corrections made.
          <div className='margin-top-1'>
            <b>Filename: </b>
            {fileName ?? 'Unknown file'}
          </div>
        </div>
      </div>
      <Accordion bordered multiselectable items={accordionItems} />
    </div>
  );
};
